import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, CheckCircle2, ChevronLeft, Download, Info, Key, Loader2, Sparkles, X } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";
import { PRACTICE_CASE_TYPES, PRACTICE_JURISDICTIONS } from "@/constants/practice";

const OpeningStatementPage = () => {
  const [form, setForm] = useState({
    case_type: 'personal_injury',
    jurisdiction: 'us_federal',
    case_summary: '',
    client_position: 'plaintiff',
    key_facts: [],
    key_witnesses: [],
    legal_theories: [],
    opposing_arguments: [],
    jury_or_bench: 'jury',
    tone: 'professional',
    statement_type: 'opening'
  });
  const [newFact, setNewFact] = useState('');
  const [newWitness, setNewWitness] = useState({ name: '', testimony: '' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const addFact = () => {
    if (!newFact.trim()) return;
    setForm(f => ({ ...f, key_facts: [...f.key_facts, newFact.trim()] }));
    setNewFact('');
  };
  
  const addWitness = () => {
    if (!newWitness.name.trim()) return;
    setForm(f => ({ ...f, key_witnesses: [...f.key_witnesses, { ...newWitness }] }));
    setNewWitness({ name: '', testimony: '' });
  };
  
  const generate = async () => {
    if (!form.case_summary.trim()) {
      setError('Please provide a case summary');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/practice/opening-statement`, form);
      setResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="opening-statement-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-5xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Opening Statements</h1>
                <Badge className="bg-blue-600">TRIAL PREP</Badge>
              </div>
              <p className="text-muted-foreground">Automated opening statement generator for trial</p>
            </div>
          </div>
        </div>
        
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {!result ? (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Case Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Case Type</Label>
                      <Select value={form.case_type} onValueChange={v => setForm(f => ({...f, case_type: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {PRACTICE_CASE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Jurisdiction</Label>
                      <Select value={form.jurisdiction} onValueChange={v => setForm(f => ({...f, jurisdiction: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {PRACTICE_JURISDICTIONS.map(j => <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-1.5">
                      <Label>Client Position</Label>
                      <Select value={form.client_position} onValueChange={v => setForm(f => ({...f, client_position: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="plaintiff">Plaintiff</SelectItem>
                          <SelectItem value="defendant">Defendant</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Trial Type</Label>
                      <Select value={form.jury_or_bench} onValueChange={v => setForm(f => ({...f, jury_or_bench: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="jury">Jury Trial</SelectItem>
                          <SelectItem value="bench">Bench Trial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Tone</Label>
                      <Select value={form.tone} onValueChange={v => setForm(f => ({...f, tone: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="professional">Professional</SelectItem>
                          <SelectItem value="aggressive">Aggressive</SelectItem>
                          <SelectItem value="empathetic">Empathetic</SelectItem>
                          <SelectItem value="narrative">Narrative</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-1.5">
                    <Label>Case Summary *</Label>
                    <Textarea 
                      placeholder="Provide a detailed summary of the case facts..."
                      value={form.case_summary}
                      onChange={e => setForm(f => ({...f, case_summary: e.target.value}))}
                      rows={5}
                    />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Key Facts to Emphasize</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2">
                    <Input placeholder="Add a key fact..." value={newFact} onChange={e => setNewFact(e.target.value)} onKeyDown={e => e.key === 'Enter' && addFact()} />
                    <Button onClick={addFact} variant="outline">Add</Button>
                  </div>
                  {form.key_facts.length > 0 && (
                    <div className="space-y-2">
                      {form.key_facts.map((fact, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-blue-50 rounded">
                          <span className="text-sm">{fact}</span>
                          <Button variant="ghost" size="sm" onClick={() => setForm(f => ({...f, key_facts: f.key_facts.filter((_, idx) => idx !== i)}))}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Key Witnesses</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-2 md:grid-cols-2">
                    <Input placeholder="Witness name..." value={newWitness.name} onChange={e => setNewWitness(w => ({...w, name: e.target.value}))} />
                    <Input placeholder="Key testimony..." value={newWitness.testimony} onChange={e => setNewWitness(w => ({...w, testimony: e.target.value}))} />
                  </div>
                  <Button onClick={addWitness} variant="outline" size="sm">Add Witness</Button>
                  {form.key_witnesses.length > 0 && (
                    <div className="space-y-2">
                      {form.key_witnesses.map((w, i) => (
                        <div key={i} className="p-2 bg-slate-50 rounded">
                          <p className="font-medium text-sm">{w.name}</p>
                          <p className="text-xs text-muted-foreground">{w.testimony}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            
            <div className="space-y-6">
              <Card className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white border-0">
                <CardContent className="p-6">
                  <Button className="w-full h-14 text-lg gap-2 bg-white text-blue-700 hover:bg-white/90" onClick={generate} disabled={loading || !form.case_summary.trim()}>
                    {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Generating...</> : <><Sparkles className="h-5 w-5" /> Generate Opening</>}
                  </Button>
                  <p className="text-xs text-white/80 mt-3 text-center">Estimated duration: 15-25 minutes</p>
                </CardContent>
              </Card>
              
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Generated statements are starting points. Always review, customize, and practice before trial.
                </AlertDescription>
              </Alert>
            </div>
          </div>
        ) : (
          <div className="space-y-6 animate-fade-in">
            <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-0">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <Badge className="bg-white/20 mb-2">{result.trial_type?.toUpperCase()} TRIAL</Badge>
                    <h2 className="text-2xl font-bold">Opening Statement Generated</h2>
                    <p className="text-white/80">{result.case_type} • {result.client_position} • Est. {result.estimated_duration}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setResult(null)} className="text-white border-white/20 hover:bg-white/10">New Statement</Button>
                    <Button variant="outline" className="text-white border-white/20 hover:bg-white/10"><Download className="h-4 w-4 mr-2" /> Export</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Opening Statement</CardTitle>
                <CardDescription>Themes: {result.themes_used?.join(', ')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none whitespace-pre-wrap">{result.content}</div>
              </CardContent>
            </Card>
            
            {result.practice_tips && (
              <Card>
                <CardHeader><CardTitle>Practice Tips</CardTitle></CardHeader>
                <CardContent>
                  <ul className="space-y-1">
                    {result.practice_tips.map((tip, i) => (
                      <li key={i} className="text-sm flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5" /> {tip}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
            
            <Alert className="border-amber-500 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-xs text-amber-800 whitespace-pre-wrap">{result.disclaimer}</AlertDescription>
            </Alert>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};

// Closing Argument Page

export default OpeningStatementPage;
